import { useAssetDetailsContext } from "@/pages/asset-details/context/assetDetailsContext";
import type { AssetAttachment } from "@/pages/asset-details/models/AssetAttachment";
import { addLinkAssetAttachments } from "@/pages/asset-details/repositories/AssetDetailsRepositorie";
import { ErrorModal, MESApiService } from "@dexteel/mesf-core";
import {
	CircularProgress,
	Dialog,
	DialogActions,
	type DialogProps,
	Divider,
	Input,
	List,
	Typography,
} from "@material-ui/core";
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import { makeStyles } from "@material-ui/core/styles";
import type { GridApi } from "ag-grid-community";
import { useEffect, useState } from "react";
import { useAssetAttachmentsGridActionsAndButtons } from "../../hooks/useAssetAttachmentsGridActionsAndButtons";
import { useUploadFiles } from "./hooks/useUploadFiles";
import DeleteForeverIcon from "@material-ui/icons/DeleteForever";

const useStyles = makeStyles((theme) => ({
	root: {
		"& .input-group-text": {
			display: "none",
		},
		"& .MuiDialogContent-dividers": {
			padding: 0,
		},
	},
	modalTitle: {
		backgroundColor: "#ced2cc",
		color: "#495057",
		textAlign: "center",
	},
	modalWarning: {
		color: "white",
		backgroundColor: "#dc3545",
		textAlign: "center",
	},
	messageTitle: {
		fontSize: 15,
		fontWeight: "bold",
		color: "#0000008A",
	},
	inputEditable: {
		"& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
			border: "2px solid #3F51B5",
		},
	},

	comments: {
		"& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
			border: "1px solid #3F51B5",
		},
	},
	formControl: {
		margin: theme.spacing(2),
		minWidth: 120,
	},
}));

type Props = {
	show: boolean;
	gridApi: GridApi | null;
	searchAssetAttachments: Function;
	setIsRemoveLinkAssetAttachmentsLoading: (isLoading: boolean) => void;
	setRemoveLinkAssetAttachmentsLoadingError: (err: string) => void;
	includeDescendants: boolean;
};

export const UploadFilePage = ({
	show,
	gridApi,
	searchAssetAttachments,
	setIsRemoveLinkAssetAttachmentsLoading,
	setRemoveLinkAssetAttachmentsLoadingError,
	includeDescendants,
}: Props) => {
	const {
		handleFileSelect,
		handleDragOver,
		handleDrop,
		handleCancel,
		setIsDragging,
		setSelectedFiles,
		selectedFiles,
	} = useUploadFiles();

	const classes = useStyles();
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [isSubmitDisabled, setIsSubmitDisabled] = useState<boolean>(false);
	const [fullWidth, setFullWidth] = useState(true);
	const [maxWidth, setMaxWidth] = useState<DialogProps["maxWidth"]>("sm");
	const [attachmentsToAdd, setAttachmentsToAdd] = useState<AssetAttachment[]>(
		[],
	);
	const {
		state: { assetSearchId },
		actions: { setUploadFileDialog },
	} = useAssetDetailsContext();

	const { refresh } = useAssetAttachmentsGridActionsAndButtons({
		gridRef: gridApi,
		searchAssetAttachments,
		setIsRemoveLinkAssetAttachmentsLoading,
		setRemoveLinkAssetAttachmentsLoadingError,
		includeDescendants,
	});
	const [
		linkAssetAttachmentsLoadingError,
		setLinkAssetAttachmentsLoadingError,
	] = useState<string>("");
	const [progressMap, setProgressMap] = useState<Record<string, number>>({});

	const handleRemoveFile = (fileNameToRemove: string) => {
		const newArr = selectedFiles.filter(
			(file: any) => file.name !== fileNameToRemove,
		);
		setSelectedFiles(newArr);
		// También eliminamos el progreso del archivo eliminado
		setProgressMap((prev) => {
			const newState = { ...prev };
			delete newState[fileNameToRemove];
			return newState;
		});
	};

	const api = new MESApiService();

	const handleProgress = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (event.target.files && event.target.files.length > 0) {
			Array.from<File>(event.target.files).forEach((file: File) => {
				const fileName = file.name;

				const reader = new FileReader();

				reader.onloadend = () => {
					setProgressMap((prevProgressMap) => ({
						...prevProgressMap,
						[fileName]: 100,
					}));
				};

				reader.onprogress = (e: ProgressEvent<FileReader>) => {
					if (e.lengthComputable) {
						const fileProgress = Math.round((e.loaded / e.total) * 100);
						setProgressMap((prevProgressMap) => ({
							...prevProgressMap,
							[fileName]: fileProgress,
						}));
					}
				};

				reader.readAsDataURL(file);
			});
		}
	};

	const handleDropWithProgress = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();

		const files = e.dataTransfer.files;

		if (files.length > 0) {
			Array.from(files).forEach((file: File) => {
				const fileName = file.name;

				const reader = new FileReader();

				reader.onloadend = () => {
					setProgressMap((prevProgressMap) => ({
						...prevProgressMap,
						[fileName]: 100,
					}));
				};

				reader.onprogress = (progressEvent: ProgressEvent<FileReader>) => {
					if (progressEvent.lengthComputable) {
						const fileProgress = Math.round(
							(progressEvent.loaded / progressEvent.total) * 100,
						);
						setProgressMap((prevProgressMap) => ({
							...prevProgressMap,
							[fileName]: fileProgress,
						}));
					}
				};

				reader.readAsDataURL(file);
			});

			handleDrop(e);
			setIsDragging(false);
		}
	};

	const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setIsSubmitLoading(true);
		if (!selectedFiles.length) {
			setIsSubmitLoading(false);
			return;
		}

		try {
			const res = await api.uploadFiles(selectedFiles, "Attachments");
			setAttachmentsToAdd(res.result);
			setIsSubmitDisabled(false); // Habilitar el botón de guardar si hay archivos.
		} catch (error: any) {
			setLinkAssetAttachmentsLoadingError(
				error.message || "Error al subir archivos",
			);
		} finally {
			setIsSubmitLoading(false);
		}
	};

	useEffect(() => {
		const processAttachments = async () => {
			if (attachmentsToAdd.length >= 1) {
				setIsSubmitLoading(true);
				let allOk = true;
				for (const att of attachmentsToAdd) {
					const res = await addLinkAssetAttachments(
						assetSearchId as string,
						att.UploadFileId as number,
						"",
					);
					if (!res.ok) {
						setLinkAssetAttachmentsLoadingError(res.message);
						allOk = false;
						break;
					}
				}

				if (allOk) {
					// Limpiar los archivos seleccionados y el progreso
					setSelectedFiles([]);
					setProgressMap({});
					refresh(); // Refrescar la grilla para mostrar los nuevos adjuntos
					setUploadFileDialog(false); // Cerrar el modal
				}
				setIsSubmitLoading(false);
				setAttachmentsToAdd([]); // Limpiar attachmentsToAdd después de procesar
			} else {
				// Solo deshabilitar el botón si no hay attachmentsToAdd y no hay selectedFiles
				if (selectedFiles.length === 0) {
					setIsSubmitDisabled(true);
				}
			}
		};

		processAttachments();
	}, [attachmentsToAdd]);

	useEffect(() => {
		// Habilitar o deshabilitar el botón de guardar basado en si hay archivos seleccionados
		setIsSubmitDisabled(selectedFiles.length < 1);
	}, [selectedFiles]);

	// Cuando el modal se cierra (show se vuelve false), limpiar los estados.
	useEffect(() => {
		if (!show) {
			setSelectedFiles([]);
			setProgressMap({});
			setAttachmentsToAdd([]);
			setIsSubmitLoading(false);
			setIsSubmitDisabled(true);
			setLinkAssetAttachmentsLoadingError("");
		}
	}, [show]);

	if (!show) return null;

	return (
		<>
			<Dialog
				onClose={() => {
					handleCancel(); // Esto ya limpia algunos estados
					setUploadFileDialog(false); // Asegura que el modal se cierra
				}}
				fullWidth={fullWidth}
				maxWidth={maxWidth}
				open={show}
				className={classes.root}
			>
				<form onSubmit={handleFormSubmit}>
					<Grid
						container
						style={{ padding: "15px 0 5px", height: "30%", overflow: "hidden" }}
					>
						<Grid
							container
							item
							md={12}
							xs={12}
							style={{
								alignItems: "center",
								marginTop: 0,
								padding: 0,
								margin: 0,
							}}
							spacing={2}
						>
							<Grid
								container
								item
								md={12}
								xs={12}
								alignItems="center"
								style={{ padding: "8px 20px 8px" }}
							>
								<Grid item md={12} xs={12}>
									<div
										className={classes.formControl}
										onDragOver={(e) => {
											handleDragOver(e);
										}}
										onDragEnter={() => setIsDragging(true)}
										onDragLeave={() => setIsDragging(false)}
										onDragEnd={() => setIsDragging(false)}
										onDrop={(e) => {
											handleDrop(e);
											setIsDragging(false);
											handleDropWithProgress(e);
										}}
										style={{
											width: "100%",
											margin: "0",
											border: "2px dashed #3F51B5",
											borderRadius: "5px",
											padding: "20px",
											textAlign: "center",
											cursor: "pointer",
										}}
										onClick={() =>
											document.getElementById("fileInput")?.click()
										}
									>
										<Typography>Drag & Drop Files here</Typography>
										<Typography>or</Typography>
										<Typography>Click Here</Typography>
										<Input
											type="file"
											onChange={(event: any) => {
												handleFileSelect(event);
												handleProgress(event);
											}}
											style={{ display: "none" }}
											id="fileInput"
											inputProps={{ multiple: true }}
										/>
									</div>
								</Grid>
							</Grid>

							{selectedFiles.length >= 1 && (
								<Grid
									container
									item
									md={12}
									xs={12}
									style={{ marginTop: "10px", marginLeft: 20, padding: "0" }}
								>
									<Grid
										item
										md={12}
										xs={12}
										style={{
											justifyContent: "flex-start !important",
											alignItems: "flex-start",
										}}
									>
										<Typography style={{ fontWeight: "bold" }}>
											Document Selected:
										</Typography>

										<List
											style={{
												height: "20vh",
												margin: "10px 20px 20px 0",
												padding: "10px",
												overflow: "auto",
											}}
										>
											{selectedFiles.map((file: any) => (
												<div key={`file-${file.name}`}>
													<div
														style={{
															textAlign: "left",
															display: "flex",
															alignItems: "center",
															padding: 0,
															margin: "5px 0",
															backgroundColor: "#FCFCFC",
														}}
													>
														<span
															style={{ marginRight: 5, cursor: "pointer" }}
															onClick={() => handleRemoveFile(file.name)}
														>
															<DeleteForeverIcon />
														</span>
														<p
															style={{
																margin: "5px 0",
																wordBreak: "break-all",
															}}
														>
															{file.name} ({(file.size / 1024).toFixed(2)} KB)
														</p>
													</div>
													{progressMap[file.name] > 0 && (
														<div
															style={{
																width: "100%",
																height: 20,
																backgroundColor: "#f2f2f2",
																borderRadius: 5,
															}}
														>
															<div
																style={{
																	width: `${progressMap[file.name]}%`,
																	height: "100%",
																	backgroundColor: `rgba(63, 81, 181, ${
																		progressMap[file.name] / 100
																	})`,
																	borderRadius: "inherit",
																	display: "flex",
																	alignItems: "center",
																	justifyContent: "center",
																	color: "white",
																	fontWeight: "bold",
																}}
															>
																<span style={{ fontSize: "12px" }}>{`${
																	progressMap[file.name]
																}%`}</span>
															</div>
														</div>
													)}
												</div>
											))}
										</List>
									</Grid>
								</Grid>
							)}
							<Divider
								variant="middle"
								style={{
									margin: "10px 0",
									width: "150%",
									marginLeft: "-100px",
								}}
							/>
							<Grid item md={12} xs={12} justifyContent="flex-end">
								<DialogActions style={{ padding: "0px 10px " }}>
									<Grid
										container
										justifyContent="flex-end"
										style={{ margin: 0 }}
									>
										<Grid
											item
											md={3}
											xs={4}
											style={{ margin: 0, padding: "2px 0 8px 0" }}
										>
											<Button
												fullWidth
												variant="contained"
												color="default"
												onClick={() => setUploadFileDialog(false)}
											>
												Cancel
											</Button>
										</Grid>
										<Grid
											item
											md={3}
											xs={4}
											style={{
												margin: 0,
												padding: "2px 0 8px 0",
												marginLeft: "20px",
											}}
										>
											<Button
												fullWidth
												startIcon={
													isSubmitLoading && <CircularProgress size="1rem" />
												}
												disabled={isSubmitLoading || isSubmitDisabled}
												variant="contained"
												color="primary"
												type="submit"
											>
												Save
											</Button>
										</Grid>
									</Grid>
								</DialogActions>
							</Grid>
						</Grid>
					</Grid>
				</form>
			</Dialog>
			<ErrorModal
				error={linkAssetAttachmentsLoadingError}
				onHide={() => setLinkAssetAttachmentsLoadingError("")}
			/>
		</>
	);
};
